import React, {useEffect} from 'react';
import styles from './ClientProjectRequestInfo.module.css';
import {
    deleteProjectRequestAPI,
    projectRequestInfoAPI,
    projectRequestSampleAPI,
    requestAgainAPI
} from "../../../services/client";
import {ProjectRequestInfoResponseData} from "../../../types/client";
import TextCard from "../../common/textCard/TextCard";
import Button from "../../common/button/Button";
import {useNavigate} from "react-router-dom";
import {RequestStatus} from "../../../types/request";
import {useAlert} from "../../common/error/AlertContext";
import logo from '../../../assets/covi_logo_white.png';

interface ClientProjectRequestInfoProps {
    projectRequestId: number;
}

//클라이언트 프로젝트 추가 요청 정보
const ClientProjectRequestInfo: React.FC<ClientProjectRequestInfoProps> = ({projectRequestId}) => {
    const [data, setData] = React.useState<ProjectRequestInfoResponseData | null>(null);
    const [sample, setSample] = React.useState<string>();
    const [loading, setLoading] = React.useState(true);
    const navigate = useNavigate();
    const {showAlert} = useAlert();
    const {showConfirm} = useAlert();

    //프로젝트 추가 요청 삭제
    const onDelete = () => {
        //확인창
        showConfirm(
            "프로젝트 요청을 삭제하시겠습니까??",
            async () => {
                //프로젝트 추가 요청 삭제
                await deleteProjectRequestAPI(projectRequestId)
                showAlert("삭제되었습니다.", "info");
                navigate("/client/projects");
            }
        )
    };

    //프로젝트 추가 요청 수정 페이지로 이동
    const handleEdit = () => {
        navigate(`/client/projects/project-request/${projectRequestId}/edit`);
    };

    //요청 처리 상태가 대기, 반려인 경우 재요청 가능
    const canRequestAgain = (status: RequestStatus | undefined): boolean => {
        return status === RequestStatus.대기 || status === RequestStatus.반려;
    };

    //요청 처리 상태가 승인인 경우 수정, 삭제 불가
    const canDelete = (status: RequestStatus | undefined): boolean => {
        return status !== RequestStatus.승인;
    }

    //데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //프로젝트 추가 요청 정보 조회 API
            const response = await projectRequestInfoAPI(projectRequestId);
            setData(response);
            //프로젝트 추가 요청 샘플 데이터 조회 API
            const file = await projectRequestSampleAPI(projectRequestId);
            //파일 내부 URL 생성
            const url = URL.createObjectURL(file);
            setSample(url);
        } catch (error) {
            console.error('Error fetching project request:', error);
        } finally {
            setLoading(false);
        }
    };

    //요일
    const formatDate = (date: Date | undefined, day: number | undefined) => {
        if (!date || day === undefined) return '-';
        const dayOfWeek = ['월', '화', '수', '목', '금', '토', '일'][day - 1];
        return `${date}(${dayOfWeek})`;
    };

    useEffect(() => {
        fetchData();
    }, [projectRequestId]);

    //요청 날짜 정보
    const requestDates = [
        {
            label: "최초 요청일",
            value: formatDate(data?.createdAt, data?.createDay)
        },
        {
            label: "마지막 수정일",
            value: data?.updatedAt ? formatDate(data?.updatedAt, data?.updateDay) : '-'
        },
        {
            label: "희망 시작일",
            value: data?.startAt ? formatDate(data?.startAt, data.startDay) : '-'
        },
        {
            label: "희망 마감일",
            value: data?.deadline ? formatDate(data?.deadline, data.deadlineDay) : '-'
        }
    ];

    //요청 정보
    const requestInfo = [
        {
            label: "처리 상태",
            value: data?.requestStatus || '-'
        },
        {
            label: "처리 일시",
            value: data?.confirmedAt ? formatDate(data?.confirmedAt, data.confirmDay) : '-'
        },
        {
            label: "데이터 수량",
            value: data?.dataAmount ? `${data.dataAmount.toLocaleString()}장` : '-'
        },
        {
            label: "가공 방식",
            value: data?.projectType || '-'
        }
    ];

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    //재요청
    const handleReRequest = async () => {
        try {
            //재요청 API
            await requestAgainAPI(projectRequestId);
            showAlert("재요청되었습니다.", "info");
            await fetchData();  // 데이터 새로고침
        } catch (error) {
            console.error('Error requesting again:', error);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <h2 className={styles.title}>Project Request Information</h2>
                <p className={styles.subtitle}>추가 요청 프로젝트 상세 정보</p>

                <div className={styles.infoContainer}>
                    <div className={styles.leftSection}>
                        <div className={styles.projectImage}>
                            {/*프로젝트 추가 요청 샘플 데이터*/}
                            {sample ? (
                                <img
                                    src={sample}
                                    alt="Project Request Preview"
                                    className={styles.image}
                                    onError={(e) => {
                                        e.currentTarget.src = logo;
                                    }}
                                />
                            ) : (
                                <div className={styles.noImage}>No image available</div>
                            )}
                        </div>
                        <div className={styles.infoGrid}>
                            {/*프로젝트 추가 요청 정보*/}
                            <TextCard items={requestDates} className={styles.fullWidth}/>
                            <TextCard items={requestInfo} className={styles.fullWidth}/>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.projectDetails}>
                            {/*프로젝트명*/}
                            <h3 className={styles.projectName}>{data?.projectName || '-'}</h3>
                            <div className={styles.explanation}>
                                {/*프로젝트 추가 요청 설명*/}
                                {data?.explanation?.split('\n').map((line, index) => (
                                    <p key={index}>{line || 'project Explanation'}</p>
                                ))}
                            </div>
                        </div>
                        <div className={styles.actionButtons}>
                            {/*재요청 버튼*/}
                            <Button
                                variant="primary"
                                size="medium"
                                onClick={handleReRequest}
                                disabled={loading || !canRequestAgain(data?.requestStatus as RequestStatus)}
                                title={
                                    !canRequestAgain(data?.requestStatus as RequestStatus)
                                        ? "대기 또는 반려 상태일 때만 재요청이 가능합니다"
                                        : ""
                                }
                            >
                                {loading ? '요청 중' : '재요청'}
                            </Button>
                            {/*수정 버튼*/}
                            <Button
                                variant="primary"
                                size="medium"
                                onClick={handleEdit}
                                disabled={loading || !canDelete(data?.requestStatus as RequestStatus)}
                                title={
                                    !canRequestAgain(data?.requestStatus as RequestStatus)
                                        ? "승인된 요청은 변경할 수 없습니다"
                                        : ""
                                }
                            >
                                수정
                            </Button>
                            {/*삭제 버튼*/}
                            <Button
                                variant="primary"
                                size="medium"
                                onClick={onDelete}
                                disabled={loading || !canDelete(data?.requestStatus as RequestStatus)}
                                title={
                                    !canRequestAgain(data?.requestStatus as RequestStatus)
                                        ? "승인된 요청은 변경할 수 없습니다"
                                        : ""
                                }
                            >
                                삭제
                            </Button>
                        </div>
                        {/*반려 상태 시 반려 사유 출력*/}
                        {data?.rejectComment && (
                            <div className={styles.comments}>
                                <h4 className={styles.commentTitle}>Comment</h4>
                                <div className={styles.commentContent}>
                                    <p>{data.rejectComment}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientProjectRequestInfo;